import { reactKindeConstants } from '@/tauri/kinde';
import { type KindeContextProps } from '@kinde-oss/kinde-auth-react';

/**
 * v5 refreshes an access token on a timer that can fire late (sleep, a
 * suspended WebView). A read of an expired token, or one inside this
 * window, refreshes before the token is returned. 30s matches the web
 * session refresh skew.
 */
const ACCESS_TOKEN_REFRESH_SKEW_SECONDS = 30;

export type KindeDesktopTokenOptions = { isForceRefresh?: boolean };

type DesktopKindeTokenSource = Pick<
  KindeContextProps,
  'getAccessToken' | 'refreshToken' | 'isLoading'
>;

function accessTokenExpirySeconds(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(base64)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Expired, inside the skew, or not a JWT with an `exp` claim. */
function accessTokenNeedsRefresh(token: string): boolean {
  const exp = accessTokenExpirySeconds(token);
  if (exp === null) return true;
  return exp <= Math.floor(Date.now() / 1000) + ACCESS_TOKEN_REFRESH_SKEW_SECONDS;
}

/**
 * Reads the desktop access token from the v5 SDK. v5 `getAccessToken()`
 * only reads the in-memory session. A forced refresh, and a read of an
 * expired or near-expiry token, calls `refreshToken` and returns its
 * access token. While the SDK is still initialising this returns null so
 * no caller surfaces a token. Null, not undefined, so the cache's
 * signed-out check stays the same.
 *
 * Every desktop read of the Kinde token goes through here: the cache's
 * desktop bridge (via `useKinde().getToken`) and the `TauriWrapper` sync
 * that writes the token into Redux.
 */
export function readDesktopKindeAccessToken(
  sdk: DesktopKindeTokenSource,
  options?: KindeDesktopTokenOptions
): Promise<string | null> | null {
  if (sdk.isLoading) {
    return null;
  }
  const refresh = (): Promise<string | null> => {
    try {
      return sdk
        .refreshToken({
          domain: reactKindeConstants.domain,
          clientId: reactKindeConstants.clientId,
        })
        .then((result) => (result.success ? (result.accessToken ?? null) : null))
        .catch(() => null);
    } catch {
      return Promise.resolve(null);
    }
  };
  if (options?.isForceRefresh) {
    return refresh();
  }
  return Promise.resolve(sdk.getAccessToken()).then(async (token) => {
    if (!token) return null;
    if (!accessTokenNeedsRefresh(token)) return token;
    const refreshed = await refresh();
    if (refreshed) return refreshed;
    const exp = accessTokenExpirySeconds(token);
    // Still inside its lifetime: a failed refresh keeps the token.
    // An expired or unreadable token is not handed out.
    if (exp !== null && exp > Math.floor(Date.now() / 1000)) return token;
    return null;
  });
}
